import {useNavigation, useRoute, useFocusEffect} from '@react-navigation/native';
import {useCallback, createElement, useRef} from 'react';
import {Platform} from 'react-native';
import {useSelector} from 'react-redux';
import {profileNotificationsSelector} from '../../redux/selectors/profile';
import {useModal} from '../../shared/components/modal';
import {defaults} from '../../shared/constants/defaults';
import {usePartialList} from '../../shared/hooks/usePartialList';
import {Screens} from '../../shared/routing';
import {NotificationDesktop} from '../notification/desktop';
import {profileNotificationsByIdSelector} from '../../redux/selectors/profile';
import {NavigationRouteProps} from '../../shared/routing/model';
import {useNotificationPressHandler} from '../notification/hooks/useNotificationPressHandler';
import View from './view';

export const NotificationsScreen = () => {
  const modal = useModal();
  const isModalOpened = useRef(false);
  const navigation = useNavigation();
  const notificationsSource = useSelector(profileNotificationsSelector());
  const [notifications, showMore, allShown] = usePartialList(
    notificationsSource,
    Platform.OS === 'web' ? defaults.partialListSizeWithButton : defaults.partialListSizeWithScroll,
  );
  const route: NavigationRouteProps<{id: string}> = useRoute();
  const notificationId = +route?.params?.id;
  const notification = useSelector(profileNotificationsByIdSelector(notificationId));
  const notificationPressHandler = useNotificationPressHandler(notification, navigation);

  const allShownWrap = useCallback(() => {
    if (!allShown) {
      showMore();
    }
  }, [allShown, showMore]);

  const checkModal = useCallback(() => {
    if (notificationId && !isModalOpened.current) {
      isModalOpened.current = true;

      modal.show({
        render({id: modalId}, methods) {
          const handleClose = () => {
            methods.remove(modalId);
            navigation.navigate(Screens.Notifications, {id: ''});
          };
          const handlePress = notificationPressHandler
            ? () => {
                methods.clear();
                notificationPressHandler();
              }
            : null;

          return createElement(NotificationDesktop, {
            onModalClose: handleClose,
            onNotificationPress: handlePress,
            id: notificationId,
            navigation,
          });
        },
      });
    }

    return () => {
      if (isModalOpened.current) {
        isModalOpened.current = false;

        const lastModal = modal.getLast();
        if (lastModal) modal.remove(lastModal.id);
      }
    };
  }, [notificationId, notificationPressHandler]);

  useFocusEffect(checkModal);

  return View({
    notifications,
    allShown,
    showMore: allShownWrap,
  });
};
